import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { Box, Breadcrumbs, Button, IconButton, styled, Toolbar, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import IconWhite from '../assets/IconWhite.png';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AppAvatar from './AppAvatar';
import MenuNav, { MenuNavProps } from './NavMenu';
import React, { useContext } from 'react';
import { SeasonShowContext } from '../Providers/SeasonShowProvider';
import { useNavigate, useParams } from 'react-router-dom';
import { FragmentType, getFragmentData, graphql } from 'gql';
import Support from './Support';

interface StyledAppBarProps extends MuiAppBarProps {
    open?: boolean
    drawerWidth: number
}

const StyledAppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open' && prop !== 'drawerWidth',
})<StyledAppBarProps>(({ theme, open, drawerWidth }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        '@media print': {
            display: 'none',
        },
    }),
    zIndex: theme.zIndex.drawer + 1,
}));

const AppBarEpisodeData = graphql(`
    fragment AppBarEpisodeData on Query {
        episodes(where: $where, options: $options) {
            id
            title
        }
    }`);

type AppBarProps = {
    open?: boolean
    drawerWidth: number
    data?: FragmentType<typeof AppBarEpisodeData>
    handleDrawerOpen: () => void
}

export default function AppBar({ open, drawerWidth, data, handleDrawerOpen }: AppBarProps) {
    const fragmentData = getFragmentData(AppBarEpisodeData, data);

    const { shows, seasons, selectedShow, selectedSeason, setSelectedShow, setSelectedSeason } = useContext(SeasonShowContext);

    const theme = useTheme();
    const navigate = useNavigate();
    const params = useParams();

    const breadcrumbs: MenuNavProps[] = [];
    breadcrumbs.push({
        label: selectedShow?.title ?? 'Select Show',
        options: shows?.map(s => ({
            label: s.title,
            onClick: () => {
                setSelectedShow(s);
                setSelectedSeason(null)
            },
        })),
    })
    if (selectedShow)
        breadcrumbs.push({
            label: selectedSeason?.title ?? 'Select Season',
            options: seasons?.map(s => ({
                label: s.title,
                onClick: () => setSelectedSeason(s),
            })),
        });
    if (selectedSeason && fragmentData && fragmentData.episodes) {
        const selectedEpisode = fragmentData.episodes.find(e => e.id === params.episodeId);

        breadcrumbs.push({
            label: selectedEpisode?.title ?? 'Select Episode',
            options: fragmentData.episodes?.map(e => ({
                label: e.title,
                onClick: () => navigate(`/episode/${e.id}`),
            })),
        });
    }

    return (
        <StyledAppBar position='fixed' open={open} drawerWidth={drawerWidth}>
            <Toolbar>
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={handleDrawerOpen}
                    edge="start"
                    sx={{ mr: 2, ...(open && { display: 'none' }) }}
                >
                    <MenuIcon />
                </IconButton>
                {!open && (
                    <img style={{ width: 30, marginRight: theme.spacing(2) }} src={IconWhite} alt='Audiolo Icon'/>
                )}
                <Breadcrumbs
                    separator={<NavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                    color='inherit'
                >
                    {breadcrumbs.map((breadcrumb, index) => (<MenuNav key={index} {...breadcrumb} />))}
                </Breadcrumbs>
                <Box sx={{ flexGrow: 1 }} />
                <Support />
                <Box sx={{ m: 1 }} />
                <Button
                    onClick={() => navigate('/organization')}
                    sx={(theme) => ({ color: theme.palette.primary.contrastText })}
                >
                    Invite My Team
                </Button>
                <Box sx={{ m: 1 }} />
                <AppAvatar />
            </Toolbar>
        </StyledAppBar>
    );
}